import { Suspense, useCallback, useEffect, useState } from 'react';
import { lazyRetry as lazy } from '@/lib/lazyRetry';
import { track } from './assistantEvents';

const AssistantPanel = lazy(() => import('./AssistantPanel'));
const AVATAR = '/veluro-avatar.jpg';
export const OPEN_EVENT = 'veluro-assistant-open';

/* Ask the assistant to open from anywhere on the public site: mode 'voice' | 'text'. */
export const openAssistant = (mode = 'text', source = 'unknown') => {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { mode, source } }));
};

/* Floating entry point for visitors (every public page) + host of the lazily loaded panel.
   Nothing plays sound or opens by itself — every open is a visitor click. */
export const VisitorAssistant = () => {
  const [open, setOpen] = useState(false);
  const [voice, setVoice] = useState(false);

  const show = useCallback((mode, source) => {
    const v = mode === 'voice';
    setVoice(v);
    setOpen(true);
    track('open', { mode: v ? 'voice' : 'text', source });
    track(v ? 'mode_voice' : 'mode_text', { source });
  }, []);

  useEffect(() => {
    const h = (e) => show(e.detail?.mode || 'text', e.detail?.source || 'event');
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, [show]);

  const close = useCallback(() => { setOpen(false); track('close'); }, []);

  return (
    <>
      {!open && (
        <button type="button" data-testid="assistant-launcher" onClick={() => show('text', 'launcher')}
          aria-label="Open Veluro AI assistant"
          className="group fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full shadow-xl shadow-violet-500/40 hover:scale-105 active:scale-95 transition-transform motion-reduce:transition-none">
          <img src={AVATAR} alt="" className="h-14 w-14 rounded-full object-cover ring-2 ring-violet-500/50 group-hover:ring-violet-300/80 transition-shadow" />
          <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-[#07070b]" aria-hidden="true" />
          <span className="pointer-events-none absolute right-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-[#0d0d14]/95 border border-white/10 px-2.5 py-1 text-[11.5px] text-white/80 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
            Ask Veluro
          </span>
        </button>
      )}
      {open && (
        <Suspense fallback={null}>
          <AssistantPanel initialVoice={voice} onClose={close} />
        </Suspense>
      )}
    </>
  );
};

export default VisitorAssistant;
